import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ShieldCheck, LogOut, Search, ExternalLink } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { fetchAdminSites } from "@/lib/api";
import AdminNavTabs from "@/components/AdminNavTabs";

const STATUS_LABEL = {
  offline_concept: "Offline Concept",
  proposal_accepted: "Proposal Accepted",
  online_client: "Online Client",
  rejected: "Rejected",
};

const STATUS_BADGE_CLASS = {
  offline_concept: "bg-slate-100 text-slate-700",
  proposal_accepted: "bg-amber-100 text-amber-800",
  online_client: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-700",
};

const CATEGORY_LABEL = {
  jewelry: "Jewelry",
  dental: "Dental",
  construction: "Construction",
  "real-estate": "Real Estate",
  "wedding-events": "Wedding & Events",
  "law-accounting": "Law & Accounting",
};

function StatusBadge({ status }) {
  return <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_BADGE_CLASS[status] || "bg-slate-100 text-slate-700"}`}>{STATUS_LABEL[status] || status}</span>;
}

export default function AdminSites() {
  const { token, email, checking, isLoggedIn, revealAdminUi, logout } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const load = () => {
    if (!token) return;
    setLoading(true);
    fetchAdminSites(token, { search: search || undefined, status: statusFilter || undefined, category: categoryFilter || undefined })
      .then(setData)
      .catch((err) => setError(err.response?.data?.detail || "Could not load the website registry."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!token) return;
    const timeout = setTimeout(load, 250); // debounce search/filter changes
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, search, statusFilter, categoryFilter]);

  const categories = useMemo(() => {
    const set = new Set((data?.items || []).map((s) => s.category));
    return [...set];
  }, [data]);

  if (checking) return null;
  if (!revealAdminUi) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2.5 text-emerald-700"><ShieldCheck size={20} /><span className="text-xs font-bold uppercase tracking-[0.16em]">Logged in as {email}</span></div>
            <h1 className="mt-3 font-display text-3xl md:text-4xl font-bold">Website Management</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">Every prospect concept and client website MySiteCare manages, in one place.</p>
          </div>
          <button type="button" onClick={logout} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"><LogOut size={15} /> Log out</button>
        </div>

        {data && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-2xl font-bold">{data.total}</p>
              <p className="text-xs text-slate-500">Total Sites</p>
            </div>
            {Object.entries(data.counts).map(([status, count]) => (
              <div key={status} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-xs text-slate-500">{STATUS_LABEL[status]}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search business name…"
              className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2.5 text-sm"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c] || c}</option>)}
          </select>
        </div>

        {error && <p className="mt-4 text-sm text-rose-600" role="status">{error}</p>}
        {loading && <p className="mt-6 text-sm text-slate-500">Loading…</p>}

        {!loading && data && (
          <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Business</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Updated</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((site) => (
                  <tr key={site.slug} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold">
                      <Link to={`/admin/sites/${site.slug}`} className="hover:text-emerald-700">{site.business_name}</Link>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{CATEGORY_LABEL[site.category] || site.category}</td>
                    <td className="px-4 py-3"><StatusBadge status={site.status} /></td>
                    <td className="px-4 py-3 text-slate-600">{site.sales_priority || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{site.selected_plan || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{site.updated_at ? new Date(site.updated_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Link to={`/admin/sites/${site.slug}`} className="text-emerald-700 font-semibold hover:underline">Manage</Link>
                        <a href={site.concept_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800"><ExternalLink size={13} /> Preview</a>
                      </div>
                    </td>
                  </tr>
                ))}
                {data.items.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No sites match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
